import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { formatError } from "../lib/api";
import { Eye, EyeSlash, Globe } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_a323fd97-8209-4ccf-b91c-22f7e7a3b086/artifacts/sfihrkca_WhatsApp%20Image%202026-04-22%20at%2022.40.09.jpeg";

export default function Login() {
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-bg flex items-center justify-center p-4">
      <div className="absolute top-5 right-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="login-lang-switcher">
              <Globe size={16} weight="duotone" />
              {i18n.language === "pt" ? "PT" : "ES"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => i18n.changeLanguage("es")}>🇪🇸 Español</DropdownMenuItem>
            <DropdownMenuItem onClick={() => i18n.changeLanguage("pt")}>🇧🇷 Português</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Card className="w-full max-w-md p-8 bg-white/90 backdrop-blur-md border-slate-200 shadow-xl fade-in-up">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="Modolo" className="w-24 h-24 rounded-2xl object-cover mb-4 shadow-md" />
          <h1 className="text-2xl heading-font font-bold text-slate-900">Modolo Multiservices S.L.</h1>
          <p className="text-sm text-slate-500 mt-1">{t("login.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              autoComplete="email"
              className="bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t("login.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                autoComplete="current-password"
                className="bg-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="toggle-password-visibility"
              >
                {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium h-11"
            data-testid="login-submit-btn"
          >
            {loading ? t("login.loading") : t("login.submit")}
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-600" />
            <div className="w-2 h-2 rounded-full bg-red-600" />
          </div>
        </div>
      </Card>
    </div>
  );
}
